import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Detect language from URL path
const getLanguageFromPath = () => {
  const path = window.location.pathname;
  if (path.startsWith('/en') || path === '/en') {
    return 'en';
  }
  return 'sl';
};

const readStoredLanguage = () => {
  try {
    return localStorage.getItem('language');
  } catch (error) {
    return null;
  }
};

const storeLanguage = (language) => {
  try {
    localStorage.setItem('language', language);
    return true;
  } catch (error) {
    return false;
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // First check URL, then localStorage
    const urlLanguage = getLanguageFromPath();
    if (urlLanguage === 'en') {
      return 'en';
    }
    return readStoredLanguage() || 'sl';
  });

  // Update language when URL changes
  useEffect(() => {
    const syncLanguageFromPath = () => {
      const urlLanguage = getLanguageFromPath();
      setLanguage((current) => current === urlLanguage ? current : urlLanguage);
    };
    syncLanguageFromPath();
    window.addEventListener('popstate', syncLanguageFromPath);
    return () => window.removeEventListener('popstate', syncLanguageFromPath);
  }, []);

  useEffect(() => {
    storeLanguage(language);
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'sl' ? 'en' : 'sl');
  }, []);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  const contextValue = useMemo(() => ({ language, setLanguage, toggleLanguage, t }), [language, setLanguage, toggleLanguage, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  sl: {
    // Header
    header: {
      services: 'Storitve',
      about: 'O nas',
      rental: 'Najem',
      blog: 'Blog',
      contact: 'Kontakt',
      cta: 'Povpraševanje',
      inquiry: 'Povpraševanje'
    },
    // Hero Section
    hero: {
      badge: 'Vaš zanesljiv partner od leta 2003',
      title: 'Celostna finančna in administrativna podpora vašemu poslu.',
      subtitle: 'Od naprednega računovodstva in vračil DDV do strokovnega svetovanja pri pridobivanju delovnih dovoljenj. Vaš strateški partner za varno in učinkovito poslovanje.',
      ctaPrimary: 'Spoznajte naše storitve',
      ctaSecondary: 'Najem poslovnih prostorov',
      stats: {
        experience: 'Let izkušenj',
        clients: 'Zadovoljnih strank',
        projects: 'Uspešnih projektov',
        googleRating: 'Google ocena'
      }
    },
    // Services Section
    services: {
      badge: 'Naše storitve',
      title: 'Celovita podpora za vaš posel',
      subtitle: 'Od ustanovitve podjetja do optimizacije davkov - z nami imate vse na enem mestu.',
      service1: {
        title: 'Ustanovitev podjetij in vstop na trg',
        description: 'Načrtujete svojo prvo poslovno pot ali širite dejavnost v Slovenijo? Pomagamo vam pri celotnem postopku ustanovitve podjetja (d.o.o., s.p. ali podružnice).',
        features: [
          'Svetovanje pri izbiri ustrezne pravnoorganizacijske oblike',
          'Priprava vse potrebne dokumentacije',
          'Svetovanje glede začetnih davčnih in računovodskih nastavitev za optimalen start'
        ],
        button: 'Želim ustanoviti podjetje – Brezplačen posvet'
      },
      service2: {
        title: 'Napredno računovodstvo in knjigovodstvo',
        description: 'Zagotavljamo natančno in ažurno vodenje poslovnih knjig, prilagojeno sodobnim digitalnim standardom.',
        features: [
          'Avtomatizirano knjiženje in brezpapirno poslovanje',
          'Priprava vseh zakonsko predpisanih letnih poročil in davčnih obračunov',
          'Specializirano vodenje vračil DDV (tudi za mednarodno poslovanje)'
        ]
      },
      service3: {
        title: 'Pridobivanje delovnih dovoljenj in kadrovska podpora',
        description: 'Specializirani smo za vodenje kompleksnih birokratskih postopkov za podjetja, ki zaposlujejo tuji kader.',
        features: [
          'Priprava dokumentacije za pridobitev in podaljšanje delovnih dovoljenj',
          'Svetovanje glede zakonodaje o zaposlovanju tujcev',
          'Celovita administrativna podpora pri komunikaciji z uradnimi organi'
        ]
      },
      service4: {
        title: 'Poslovno in davčno svetovanje',
        description: 'Vaš partner pri sprejemanju ključnih finančnih odločitev.',
        features: [
          'Optimizacija davčnih obveznosti v skladu z veljavno zakonodajo',
          'Priprava finančnih analiz in načrtovanje denarnega toka (Cash-flow)',
          'Podpora pri statusnih spremembah podjetij'
        ]
      }
    },
    // About Section
    about: {
      badge: 'O nas',
      title: 'Tradicija izkušenj, vizija prihodnosti',
      intro: 'V podjetju General Ledger d.o.o. verjamemo, da so kakovostni podatki temelj vsake uspešne poslovne zgodbe. Smo ekipa predanih strokovnjakov z večletnimi izkušnjami na področju računovodstva, knjigovodstva in finančnega svetovanja.',
      description: 'Naša pot temelji na nudenju celovite podpore podjetjem vseh velikosti – od ambicioznih startupov do uveljavljenih mednarodnih družb. Skozi leta smo svoje znanje nadgradili in razširili na specifična področja, kot so vračila DDV, pridobivanje delovnih dovoljenj in strateško davčno načrtovanje.',
      whyUsTitle: 'Zakaj izbrati nas?',
      reasons: [
        {
          title: 'Strokovnost z zgodovino',
          description: 'Naše dolgoletne izkušnje nam omogočajo, da predvidimo izzive, še preden postanejo težave.'
        },
        {
          title: 'Več kot le številke',
          description: 'Ne le, da knjižimo vaše račune, temveč vam s svetovanjem pomagamo razumeti vaše finance in optimizirati poslovanje.'
        },
        {
          title: 'Prilagodljivost',
          description: 'Vsako podjetje je unikatno, zato naš pristop vedno prilagodimo vašim specifičnim potrebam in panogi.'
        },
        {
          title: 'Celostna oskrba',
          description: 'Smo ena redkih točk, kjer lahko uredite vse – od statusnih vprašanj zaposlenih in računovodstva do najema sodobnih poslovnih prostorov za vašo ekipo.'
        }
      ],
      mission: 'Naše poslanstvo je preprosto: Razbremeniti vas birokracije, da se vi lahko posvetite tistemu, kar počnete najbolje – rasti svojega posla.'
    },
    // Rental Section
    rental: {
      badge: 'Poslovni prostori',
      title: 'Ekskluzivni poslovni prostori za vašo dejavnost',
      description: 'Oddajamo lastne, sodobno opremljene poslovne prostore na strateških lokacijah. Zagotovite si stabilno in reprezentativno okolje za razvoj svoje ekipe.',
      features: {
        direct: {
          title: 'Direktni najem brez posrednikov',
          description: 'Brez dodatnih stroškov posredovanja'
        },
        flexible: {
          title: 'Fleksibilne možnosti prilagoditve',
          description: 'Prostori prilagojeni vašim potrebam'
        },
        support: {
          title: 'Vsa podpora na enem mestu',
          description: 'Računovodstvo in podpora v isti stavbi'
        }
      },
      location: 'Lokacija',
      size: 'Velikost',
      price: 'Cena',
      byAgreement: 'Po dogovoru',
      contactUs: 'Kontaktirajte nas'
    },
    // Blog Section
    blog: {
      badge: 'Novice & Blog',
      title: 'Najnovejše objave',
      archiveTitle: 'Arhiv novic',
      subtitle: 'Spremljajte najnovejše spremembe zakonodaje in koristne nasvete za vaše poslovanje.',
      readMore: 'Preberi več',
      archive: 'Arhiv novic',
      backToNews: 'Nazaj na novice'
    },
    // Contact Form
    contact: {
      badge: 'Kontakt',
      title: 'Povpraševanje za storitve',
      subtitle: 'Izpolnite obrazec in naši strokovnjaki vas bodo kontaktirali v najkrajšem možnem času.',
      formTitle: 'Povpraševanje',
      formSubtitle: 'Izpolnite obrazec in odgovorili vam bomo v najkrajšem možnem času.',
      quickResponse: 'Hiter odziv',
      quickResponseDesc: 'Odgovorimo v 24 urah',
      fullServices: 'Celovite storitve',
      fullServicesDesc: 'Računovodstvo, svetovanje, delovna dovoljenja in več',
      fields: {
        companyName: 'Ime podjetja',
        contactPerson: 'Kontaktna oseba',
        email: 'E-pošta',
        phone: 'Telefon',
        serviceType: 'Vrsta storitve',
        selectService: 'Izberite storitev',
        message: 'Sporočilo',
        messagePlaceholder: 'Opišite vaše potrebe...',
        attachment: 'Priponka (neobvezno)',
        addAttachment: 'Dodaj priponko (max 3 MB)',
        allowedFormats: 'Dovoljene oblike: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, TXT'
      },
      services: {
        accounting: 'Računovodstvo',
        workPermits: 'Delovna dovoljenja',
        consulting: 'Svetovanje',
        vatRefunds: 'Vračila DDV',
        rental: 'Najem poslovnega prostora',
        other: 'Ostalo'
      },
      privacy: 'Strinjam se z obdelavo osebnih podatkov v skladu z',
      privacyLink: 'Obvestilom o zasebnosti',
      submit: 'Pošlji povpraševanje',
      sending: 'Pošiljanje...',
      success: 'Hvala za vaše povpraševanje!',
      successDesc: 'Kontaktirali vas bomo v najkrajšem možnem času.',
      fileTooLarge: 'Datoteka je prevelika. Največja dovoljena velikost je 3 MB.',
      privacyRequired: 'Prosimo, potrdite soglasje z obdelavo osebnih podatkov.',
      serviceRequired: 'Prosimo, izberite vrsto storitve.',
      successToast: 'Povpraševanje uspešno poslano!',
      errorToast: 'Napaka pri pošiljanju. Prosimo, poskusite znova.'
    },
    // Footer
    footer: {
      description: 'Vaš zanesljiv partner za celovite finančne in administrativne storitve. Z nami je vaše poslovanje v varnih rokah.',
      services: 'Storitve',
      company: 'Podjetje',
      contactTitle: 'Kontakt',
      workingHours: 'Pon - Pet: 8:00 - 16:00',
      allRights: 'Vse pravice pridržane.',
      privacy: 'Obvestilo o zasebnosti',
      serviceLinks: {
        accounting: 'Računovodstvo',
        taxConsulting: 'Davčno svetovanje',
        workPermits: 'Delovna dovoljenja',
        adminServices: 'Administrativne storitve'
      },
      companyLinks: {
        about: 'O nas',
        rental: 'Poslovni prostori',
        blog: 'Blog',
        contact: 'Kontakt'
      }
    },
    // Blog posts
    blogPosts: {
      post1: {
        title: 'Novosti 2026: Rekordna minimalna plača in osvežene višine dnevnic',
        excerpt: 'S 1. januarjem 2026 stopajo v veljavo ključne spremembe pri obračunu plač in stroškov dela. Poleg novega zneska minimalne plače, ki prvič presega 1.000 € neto, so se uskladile tudi višine povračil stroškov za službene poti.',
        category: 'Plače'
      },
      post2: {
        title: 'Nove višine dnevnic za tujino in Slovenijo s 1. 1. 2026',
        excerpt: 'Z novim letom je stopila v veljavo nova Uredba o povračilu stroškov za službena potovanja v tujino, ki zvišuje zneske dnevnic in posodablja časovne prage za obračun.',
        category: 'Potni stroški'
      }
    },
    // Blog Post Page
    blogPost: {
      backToBlog: 'Nazaj na blog',
      notFound: 'Članek ni najden',
      notFoundDesc: 'Žal članka, ki ga iščete, nismo našli.',
      needHelp: 'Potrebujete strokovno pomoč?',
      needHelpDesc: 'Naši strokovnjaki vam lahko pomagajo pri razumevanju zakonodajnih sprememb in optimizaciji vašega poslovanja.',
      contactUs: 'Kontaktirajte nas'
    }
  },
  en: {
    // Header
    header: {
      services: 'Services',
      about: 'About',
      rental: 'Rental',
      blog: 'Blog',
      contact: 'Contact',
      cta: 'Inquiry',
      inquiry: 'Free consultation'
    },
    // Hero Section
    hero: {
      badge: 'Your reliable partner since 2003',
      title: 'Comprehensive financial and administrative support for your business.',
      subtitle: 'From advanced accounting and VAT refunds to expert consulting for obtaining work permits. Your strategic partner for safe and efficient business operations.',
      ctaPrimary: 'Discover our services',
      ctaSecondary: 'Business premises rental',
      stats: {
        experience: 'Years of experience',
        clients: 'Satisfied clients',
        projects: 'Successful projects',
        googleRating: 'Google rating'
      }
    },
    // Services Section
    services: {
      badge: 'Our Services',
      title: 'Comprehensive support for your business',
      subtitle: 'From company establishment to tax optimization - with us you have everything in one place.',
      service1: {
        title: 'Company establishment and market entry',
        description: 'Planning your first business venture or expanding into Slovenia? We help you with the entire company establishment process (d.o.o., s.p. or branch office).',
        features: [
          'Advice on choosing the appropriate legal organizational form',
          'Preparation of all necessary documentation',
          'Advice on initial tax and accounting settings for optimal start'
        ],
        button: 'I want to establish a company – Free consultation'
      },
      service2: {
        title: 'Advanced accounting and bookkeeping',
        description: 'We provide accurate and up-to-date business bookkeeping adapted to modern digital standards.',
        features: [
          'Automated bookkeeping and paperless operations',
          'Preparation of all legally required annual reports and tax calculations',
          'Specialized VAT refund management (including international business)'
        ]
      },
      service3: {
        title: 'Work permit acquisition and HR support',
        description: 'We specialize in managing complex bureaucratic procedures for companies employing foreign staff.',
        features: [
          'Documentation preparation for obtaining and extending work permits',
          'Advice on legislation regarding employment of foreigners',
          'Comprehensive administrative support in communication with official authorities'
        ]
      },
      service4: {
        title: 'Business and tax consulting',
        description: 'Your partner in making key financial decisions.',
        features: [
          'Tax liability optimization in accordance with applicable legislation',
          'Financial analysis preparation and cash-flow planning',
          'Support for company status changes'
        ]
      }
    },
    // About Section
    about: {
      badge: 'About Us',
      title: 'Tradition of experience, vision of the future',
      intro: 'At General Ledger d.o.o., we believe that quality data is the foundation of every successful business story. We are a team of dedicated experts with many years of experience in accounting, bookkeeping and financial consulting.',
      description: 'Our path is based on providing comprehensive support to companies of all sizes - from ambitious startups to established international companies. Over the years, we have upgraded and expanded our knowledge to specific areas such as VAT refunds, work permit acquisition and strategic tax planning.',
      whyUsTitle: 'Why choose us?',
      reasons: [
        {
          title: 'Expertise with history',
          description: 'Our many years of experience allow us to anticipate challenges before they become problems.'
        },
        {
          title: 'More than just numbers',
          description: 'We not only book your invoices, but also help you understand your finances and optimize your business through consulting.'
        },
        {
          title: 'Adaptability',
          description: 'Every company is unique, so we always adapt our approach to your specific needs and industry.'
        },
        {
          title: 'Comprehensive care',
          description: 'We are one of the few places where you can arrange everything - from employee status issues and accounting to renting modern office space for your team.'
        }
      ],
      mission: 'Our mission is simple: To relieve you of bureaucracy so you can focus on what you do best - growing your business.'
    },
    // Rental Section
    rental: {
      badge: 'Business Premises',
      title: 'Exclusive business premises for your activity',
      description: 'We rent our own, modernly equipped business premises in strategic locations. Ensure a stable and representative environment for the development of your team.',
      features: {
        direct: {
          title: 'Direct rental without intermediaries',
          description: 'No additional brokerage costs'
        },
        flexible: {
          title: 'Flexible customization options',
          description: 'Spaces adapted to your needs'
        },
        support: {
          title: 'All support in one place',
          description: 'Accounting and support in the same building'
        }
      },
      location: 'Location',
      size: 'Size',
      price: 'Price',
      byAgreement: 'By agreement',
      contactUs: 'Contact us'
    },
    // Blog Section
    blog: {
      badge: 'News & Blog',
      title: 'Latest posts',
      archiveTitle: 'News archive',
      subtitle: 'Follow the latest legislative changes and useful tips for your business.',
      readMore: 'Read more',
      archive: 'News archive',
      backToNews: 'Back to news'
    },
    // Contact Form
    contact: {
      badge: 'Contact',
      title: 'Service inquiry',
      subtitle: 'Fill out the form and our experts will contact you as soon as possible.',
      formTitle: 'Inquiry',
      formSubtitle: 'Fill out the form and we will respond as soon as possible.',
      quickResponse: 'Quick response',
      quickResponseDesc: 'We respond within 24 hours',
      fullServices: 'Comprehensive services',
      fullServicesDesc: 'Accounting, consulting, work permits and more',
      fields: {
        companyName: 'Company name',
        contactPerson: 'Contact person',
        email: 'Email',
        phone: 'Phone',
        serviceType: 'Service type',
        selectService: 'Select service',
        message: 'Message',
        messagePlaceholder: 'Describe your needs...',
        attachment: 'Attachment (optional)',
        addAttachment: 'Add attachment (max 3 MB)',
        allowedFormats: 'Allowed formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, TXT'
      },
      services: {
        accounting: 'Accounting',
        workPermits: 'Work permits',
        consulting: 'Consulting',
        vatRefunds: 'VAT refunds',
        rental: 'Business premises rental',
        other: 'Other'
      },
      privacy: 'I agree to the processing of personal data in accordance with the',
      privacyLink: 'Privacy Notice',
      submit: 'Send inquiry',
      sending: 'Sending...',
      success: 'Thank you for your inquiry!',
      successDesc: 'We will contact you as soon as possible.',
      fileTooLarge: 'File is too large. Maximum allowed size is 3 MB.',
      privacyRequired: 'Please confirm your consent to personal data processing.',
      serviceRequired: 'Please select a service type.',
      successToast: 'Inquiry sent successfully!',
      errorToast: 'Error sending. Please try again.'
    },
    // Footer
    footer: {
      description: 'Your reliable partner for comprehensive financial and administrative services. Your business is in safe hands with us.',
      services: 'Services',
      company: 'Company',
      contactTitle: 'Contact',
      workingHours: 'Mon - Fri: 8:00 - 16:00',
      allRights: 'All rights reserved.',
      privacy: 'Privacy Notice',
      serviceLinks: {
        accounting: 'Accounting',
        taxConsulting: 'Tax consulting',
        workPermits: 'Work permits',
        adminServices: 'Administrative services'
      },
      companyLinks: {
        about: 'About us',
        rental: 'Business premises',
        blog: 'Blog',
        contact: 'Contact'
      }
    },
    // Blog posts
    blogPosts: {
      post1: {
        title: '2026 Updates: Record minimum wage and updated per diem rates',
        excerpt: 'Key changes in salary calculations and labor costs come into effect on January 1, 2026. In addition to the new minimum wage amount, which for the first time exceeds €1,000 net, the rates of reimbursements for business trips have also been updated.',
        category: 'Salaries'
      },
      post2: {
        title: 'New per diem rates for abroad and Slovenia from January 1, 2026',
        excerpt: 'With the new year, a new Regulation on reimbursement of costs for business trips abroad has come into force, increasing per diem amounts and updating time thresholds for calculation.',
        category: 'Travel expenses'
      }
    },
    // Blog Post Page
    blogPost: {
      backToBlog: 'Back to blog',
      notFound: 'Article not found',
      notFoundDesc: 'Sorry, we could not find the article you are looking for.',
      needHelp: 'Need expert help?',
      needHelpDesc: 'Our experts can help you understand legislative changes and optimize your business operations.',
      contactUs: 'Contact us'
    }
  }
};

export default LanguageContext;
